import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsDropdownComponent,
  YdsEmptyStatesComponent,
  YdsSpinnerComponent,
  YdsTableComponent,
  YdsSplitButtonComponent,
  YdsTableCellDirective,
  YdsPaginationComponent,
  type YdsDropdownOption,
  type YdsTableColumn,
  type YdsSplitButtonItem,
} from '../../../yds-angular';
import { formatDisplayDate } from '../../../shared/general-ledger-yds.utils';
import { JournalProcessDefinition } from '../../../model/journal-process-definition.model';
import { BookingGroup } from '../../../model/booking-group.model';
import { JournalProcessDefinitionService } from '../journalprocessdefinition.service';
import { BookingGroupService } from '../../bookinggroup/bookinggroup.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteJournalProcessDefinitionWithConfirm } from '../journalprocessdefinition-shared';

@Component({
  selector: 'app-journalprocessdefinition-list',
  imports: [
    YdsCardComponent,
    YdsButtonComponent,
    YdsDropdownComponent,
    YdsTableComponent,
    YdsSpinnerComponent,
    YdsEmptyStatesComponent,
    YdsSplitButtonComponent,
    YdsTableCellDirective,
    YdsPaginationComponent,
  ],
  templateUrl: './journalprocessdefinition-list.component.html',
  styleUrl: './journalprocessdefinition-list.component.scss',
})
export class JournalprocessdefinitionListComponent implements OnInit {
  readonly pageSize = 10;
  readonly tableColumns: YdsTableColumn[] = [
    { key: 'name', header: 'Verwerkingsdefinitie' },
    { key: 'journalNumber', header: 'Dagboeknummer' },
    { key: 'startDate', header: 'Begindatum' },
    { key: 'endDate', header: 'Einddatum' },
    { key: 'actions', header: 'Actieknop', width: '160px' },
  ];

  readonly splitButtonItems: readonly YdsSplitButtonItem[] = [
    { id: 'edit', label: 'Wijzigen' },
    { id: 'copy', label: 'Kopiëren', disabled: true },
    { id: 'delete', label: 'Verwijderen' },
  ];

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private journalProcessDefinitionService = inject(JournalProcessDefinitionService);
  private bookingGroupService = inject(BookingGroupService);
  private dialogService = inject(ConfirmDialogService);
  private destroyRef = inject(DestroyRef);

  bookingGroups: WritableSignal<BookingGroup[]> = signal([]);
  bookingGroupId = signal<string>('');
  isLoadingGroups = signal(true);
  journalProcessDefinitions: WritableSignal<JournalProcessDefinition[]> = signal([]);
  pageIndex = signal(0);
  isLoading = signal(true);

  bookingGroupOptions = computed<YdsDropdownOption[]>(() =>
    this.bookingGroups().map((group) => ({
      id: group.bookingGroupId,
      label: group.bookingGroup_name,
    })),
  );

  private reload$ = new Subject<void>();

  pagedDefinitions = computed(() => {
    const start = this.pageIndex() * this.pageSize;
    return this.journalProcessDefinitions().slice(start, start + this.pageSize);
  });

  tableItems = computed(() =>
    this.pagedDefinitions().map((item) => ({
      id: item.journalProcessDefinitionId,
      bookingGroupId: item.bookingGroupId,
      name: item.name,
      journalNumber: item.journalNumber ?? '',
      startDate: formatDisplayDate(item.startDate, 'nl-NL'),
      endDate: this.formatEndDate(item.endDate),
    })),
  );

  private formatEndDate(endDate: JournalProcessDefinition['endDate']): string {
    const formatted = formatDisplayDate(endDate, 'nl-NL');
    return formatted === '31-12-9999' ? 'n.v.t' : formatted;
  }

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);
          this.journalProcessDefinitions.set([]);
          const groupId = this.bookingGroupId();
          if (!groupId) {
            this.isLoading.set(false);
            return EMPTY;
          }
          return this.journalProcessDefinitionService.getByContext(groupId).pipe(
            catchError(() => {
              this.isLoading.set(false);
              this.journalProcessDefinitions.set([]);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (items) => {
          this.journalProcessDefinitions.set(items);
          this.pageIndex.set(0);
          this.isLoading.set(false);
        },
      });
  }

  ngOnInit(): void {
    this.loadBookingGroups();
  }

  loadBookingGroups(): void {
    this.isLoadingGroups.set(true);
    this.bookingGroupService
      .getByContext()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (groups) => {
          this.bookingGroups.set(groups);
          this.isLoadingGroups.set(false);
          if (groups.length > 0) {
            // Select the booking group from the route when present, otherwise the first one.
            const routeGroupId = this.route.snapshot.paramMap.get('bookingGroupId');
            const selected = groups.find((group) => group.bookingGroupId === routeGroupId);
            this.onBookingGroupChange((selected ?? groups[0]).bookingGroupId);
          } else {
            this.bookingGroupId.set('');
            this.isLoading.set(false);
          }
        },
        error: () => {
          this.bookingGroups.set([]);
          this.isLoadingGroups.set(false);
          this.isLoading.set(false);
        },
      });
  }

  onBookingGroupChange(bookingGroupId: string | null): void {
    this.bookingGroupId.set(bookingGroupId ?? '');
    this.loadJournalProcessDefinitions();
  }

  loadJournalProcessDefinitions(): void {
    this.reload$.next();
  }

  navigateToCreate(): void {
    this.router.navigate(['/config/journalprocessdefinition/edit'], {
      state: { bookingGroupId: this.bookingGroupId() },
    });
  }

  onTableRowClick(row: Record<string, unknown>): void {
    // Default action when clicking a row is "Bekijk details".
    this.viewItem(String(row['id']));
  }

  viewItem(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.router.navigate(['/config/journalprocessdefinition/edit', id]);
  }

  handleActionClick(id: string, actionId: string): void {
    if (actionId === 'edit') {
      this.viewItem(id);
      return;
    }
    if (actionId === 'delete') {
      this.deleteItem(id);
      return;
    }
  }

  private deleteItem(id: string): void {
    deleteJournalProcessDefinitionWithConfirm({
      id,
      service: this.journalProcessDefinitionService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.loadJournalProcessDefinitions(),
    });
  }
}
